/**
 * TimelineViewToggle Component
 *
 * Phase/Task visibility toggle buttons.
 * Controls what items are visible on the timeline.
 */

import React, { useEffect, useRef } from 'react'
import { useMediaQuery, useTheme } from '@mui/material'
import * as Icons from '@mui/icons-material'
import { TpToggleButtonGroup } from '~/components/tp-toggle-button-group'
import { TpToggleButton } from '~/components/tp-toggle-button'
import { TimelineToggleWithPopper } from './timeline-toggle-with-popper'
import {
  useTimelineContextDispatch,
  setViewStrategy,
} from '../../context/timeline-context'
import { useTimelineStaticContext } from '../../context/timeline-static-context'
import { CONTROLS_BREAKPOINT } from './timeline-controls-constants'
import { TpControlTooltip } from '../../../../../components/tp-control-tooltip'
import { useOrgHoverContext } from '~/common/context/organization/org-context'
import { useEditCropContext } from '~/crop/edit-crop/edit-crop-context'

/**
 * TimelineViewToggle - View strategy buttons
 *
 * Options:
 * - Phases: Show only crop growth phases
 * - Tasks: Show only tasks
 * - Both: Show phases + tasks (tasks below phases)
 */
export const TimelineViewToggle = () => {
  const theme = useTheme()
  const isExpanded = useMediaQuery(theme.breakpoints.up(CONTROLS_BREAKPOINT))
  const { viewStrategy } = useTimelineStaticContext()
  const dispatch = useTimelineContextDispatch()
  const { hoveredTask } = useOrgHoverContext()
  const editCtx = useEditCropContext()
  const isEditing = editCtx?.editingCropId !== null
  
  const prevViewStrategyRef = useRef(viewStrategy)

  useEffect(() => {
    // editing has precedent
    if (isEditing) return

    if (hoveredTask !== null && prevViewStrategyRef.current === 'phases') {
      dispatch(setViewStrategy({ viewStrategy: 'tasks' }))
    } else {
      dispatch(setViewStrategy({ viewStrategy: prevViewStrategyRef.current ?? 'phases' }))
    }
  }, [dispatch, isEditing, hoveredTask])

  const handleChange = (e, newValue) => {
    if (newValue !== null) {
      prevViewStrategyRef.current = newValue
      dispatch(setViewStrategy({ viewStrategy: newValue }))
    }
  }

  if (isExpanded) {
    return (
      <TpControlTooltip title="View" offsetBottom={-8}>
        <span>
          <TimelineViewToggleContent {...{ viewStrategy, handleChange }} />
        </span>
      </TpControlTooltip>
    )
  }

  return (
    <TimelineToggleWithPopper
      title="View"
      icon={<Icons.FilterList fontSize='small' />}
    >
      <TimelineViewToggleContent {...{ viewStrategy, handleChange }} />
    </TimelineToggleWithPopper>
  )
}

const TimelineViewToggleContent = (props) => {
  return (
    <TpToggleButtonGroup
      exclusive
      value={props.viewStrategy}
      onChange={props.handleChange}
      sx={{
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <TpToggleButton value="phases">Phases</TpToggleButton>
      <TpToggleButton value="tasks">Tasks</TpToggleButton>
      <TpToggleButton value="both">Both</TpToggleButton>
    </TpToggleButtonGroup>
  )
}
